// =============================================================================
// meta.ts — Meta-progressão persistente entre partidas (issues #90 e #94):
// - VOTOS VITALÍCIOS: todo voto (XP) coletado em partida soma num total por
//   conta que NUNCA é gasto — só acumula e destrava candidatos e armas da urna
//   por marcos (o "Curral Eleitoral").
// - CAIXA 2: moeda gastável que dropa de elites/bosses e paga os upgrades
//   permanentes do Gabinete de Campanha.
// Só DADOS e helpers puros: a aplicação dos bônus é autoritativa no servidor
// (src/server/domain/metaUpgrades.ts) e a persistência mora em Db.ts.
// =============================================================================
import { freshFeitosState, sanitizeFeitosState, type FeitosState } from './feitos';
import { fmtNum, fmtPct } from './upgrades';

// -----------------------------------------------------------------------------
// Perfil persistente (Postgres quando logado; localStorage do navegador quando
// anônimo ou em modo dev sem banco — issues #90/#94)
// -----------------------------------------------------------------------------
export interface MetaProfile {
  votos: number;                      // votos vitalícios (nunca gastos)
  caixa2: number;                     // saldo gastável do Caixa 2
  upgrades: Record<string, number>;   // upgradeId -> nível comprado no Gabinete
}

// O que a simulação precisa saber de cada jogador no INÍCIO da partida.
// Os feitos (#99/#92/#95) entram junto: estatísticas vitalícias, conquistas
// cumpridas (travas de arma da urna) e as pastas de dossiê em andamento.
export interface PlayerMetaState {
  votos: number;
  upgrades: Record<string, number>;
  feitos: FeitosState;
}

// Progresso dotado: conta nova já começa com o primeiro marco ~20% cheio
// ("votos de boca de urna") — medidor zerado desanima até cabo eleitoral
export const VOTOS_BOCA_URNA = 1000;

export function freshMetaState(): PlayerMetaState {
  return { votos: VOTOS_BOCA_URNA, upgrades: {}, feitos: freshFeitosState() };
}

export function freshMetaProfile(): MetaProfile {
  return { votos: VOTOS_BOCA_URNA, caixa2: 0, upgrades: {} };
}

// Inteiro não-negativo ou zero — nada de NaN, negativo ou float vindo de fora
function nonNegInt(v: unknown): number {
  const n = Math.floor(Number(v));
  return Number.isFinite(n) && n > 0 ? n : 0;
}

// Sanitiza um perfil vindo de FORA do servidor (localStorage do cliente ou
// payload de join de anônimo): só ids conhecidos, níveis clampados na tabela,
// números inteiros não-negativos. O que não passa é descartado.
export function sanitizeMetaProfile(raw: unknown): MetaProfile {
  const r = (typeof raw === 'object' && raw !== null ? raw : {}) as Record<string, unknown>;
  const upgrades: Record<string, number> = {};
  if (typeof r.upgrades === 'object' && r.upgrades !== null) {
    for (const [id, lvl] of Object.entries(r.upgrades as Record<string, unknown>)) {
      const def = META_UPGRADE_TABLE[id];
      if (!def) continue;
      const n = Math.min(nonNegInt(lvl), def.max);
      if (n > 0) upgrades[id] = n;
    }
  }
  return { votos: nonNegInt(r.votos), caixa2: nonNegInt(r.caixa2), upgrades };
}

// Versão pro join de sala (o Caixa 2 não interessa à simulação). Os feitos
// (#99/#92/#95) do anônimo viajam junto e passam pelo próprio sanitizador.
export function sanitizeMetaState(raw: unknown): PlayerMetaState {
  const p = sanitizeMetaProfile(raw);
  const r = (typeof raw === 'object' && raw !== null ? raw : {}) as Record<string, unknown>;
  return { votos: p.votos, upgrades: p.upgrades, feitos: sanitizeFeitosState(r.feitos) };
}

// -----------------------------------------------------------------------------
// Curral Eleitoral: marcos de votos vitalícios destravam candidatos e armas
// -----------------------------------------------------------------------------
export interface MetaUnlockDef {
  kind: 'character' | 'weapon';
  id: string;         // CharacterId ou WeaponId (strings pra evitar import circular)
  votos: number;      // total vitalício necessário
  nome: string;       // rótulo exibido no Gabinete
}

// Ordenado por custo crescente (o Gabinete mostra o próximo da fila)
export const META_UNLOCKS: MetaUnlockDef[] = [
  { kind: 'character', id: 'temer', votos: 250000, nome: 'Vampirão' },
  { kind: 'weapon', id: 'oito_jan', votos: 400000, nome: '8 de Janeiro (arma da urna)' },
  { kind: 'character', id: 'padrekelmon', votos: 750000, nome: 'Padre de Festa Junina' },
  { kind: 'weapon', id: 'stf', votos: 1250000, nome: 'Decisão do STF (arma da urna)' },
  { kind: 'character', id: 'pablomarcal', votos: 2000000, nome: 'Ex-Coach' },
];

function unlockAt(kind: MetaUnlockDef['kind'], id: string): number | null {
  const u = META_UNLOCKS.find((m) => m.kind === kind && m.id === id);
  return u ? u.votos : null;   // null = nunca esteve sob sigilo (sempre liberado)
}

export function charUnlockVotes(charId: string): number | null {
  return unlockAt('character', charId);
}

export function isCharUnlocked(votos: number, charId: string): boolean {
  const need = charUnlockVotes(charId);
  return need === null || votos >= need;
}

export function isWeaponUnlocked(votos: number, weaponId: string): boolean {
  const need = unlockAt('weapon', weaponId);
  return need === null || votos >= need;
}

// Próximo marco ainda fechado (null = curral completo, virou cacique)
export function nextUnlock(votos: number): MetaUnlockDef | null {
  return META_UNLOCKS.find((m) => votos < m.votos) ?? null;
}

// -----------------------------------------------------------------------------
// Gabinete de Campanha: upgrades permanentes comprados com Caixa 2 (issue #94)
// -----------------------------------------------------------------------------
export interface MetaUpgradeDef {
  id: string;
  nome: string;
  desc: string;
  passo: number;       // incremento por nível (fração p/ %, pontos p/ HP)
  max: number;         // nível máximo
  custoBase: number;   // custo do nível 1; os seguintes escalam linearmente
}

// Passos declarados ANTES da tabela pra desc interpolar o MESMO número que o
// servidor aplica (src/server/domain/metaUpgrades.ts lê `passo`) — fonte única.
//
// Progressão estilo Vampire Survivors (balance 2026-07): zero-spend continua
// hard (curvas de tempo intocadas); cada nível do Gabinete deve ser SENTIDO
// na próxima run. Full stack mira ~×2.1 combate (dmg×atkSpeed) e +100 HP;
// custos baixos cedo / caros no fim, combat/tank baratos antes do Fundo.
const PASSO = {
  verba_gabinete: 0.10,    // +10%/nv → +50% dmg no max (era +5%/nv)
  plano_saude: 20,         // +20 HP/nv → +100 no max (era +10)
  carro_oficial: 0.05,     // +5%/nv → +15% speed no max (era +3%)
  assessor_imprensa: 0.08, // +8%/nv → +40% atk speed no max (era +4%)
  base_aliada: 0.12,       // +12%/nv → +36% pickup no max (era +8%)
  fundo_eleitoral: 0.08,   // +8%/nv → +40% Caixa 2 no max (era +5%)
};

export const META_UPGRADES = {
  verba_gabinete: {
    id: 'verba_gabinete',
    nome: 'Verba de Gabinete',
    desc: `+${fmtPct(PASSO.verba_gabinete)}% de dano por nível. Emenda impositiva direto no coldre.`,
    passo: PASSO.verba_gabinete,
    max: 5,
    custoBase: 300,
  },
  plano_saude: {
    id: 'plano_saude',
    nome: 'Plano de Saúde Parlamentar',
    desc: `+${fmtNum(PASSO.plano_saude)} de HP máximo por nível. Cobertura total, carência zero, só pra você.`,
    passo: PASSO.plano_saude,
    max: 5,
    custoBase: 250,
  },
  carro_oficial: {
    id: 'carro_oficial',
    nome: 'Carro Oficial',
    desc: `+${fmtPct(PASSO.carro_oficial)}% de velocidade por nível. Placa fria, sirene quente.`,
    passo: PASSO.carro_oficial,
    max: 3,
    custoBase: 350,
  },
  assessor_imprensa: {
    id: 'assessor_imprensa',
    nome: 'Assessor de Imprensa',
    desc: `+${fmtPct(PASSO.assessor_imprensa)}% de velocidade de ataque por nível. A nota sai antes do fato.`,
    passo: PASSO.assessor_imprensa,
    max: 5,
    custoBase: 300,
  },
  base_aliada: {
    id: 'base_aliada',
    nome: 'Base Aliada',
    desc: `+${fmtPct(PASSO.base_aliada)}% de raio de coleta de votos por nível. O interior vota com a gente.`,
    passo: PASSO.base_aliada,
    max: 3,
    custoBase: 280,
  },
  fundo_eleitoral: {
    id: 'fundo_eleitoral',
    nome: 'Fundo Eleitoral',
    desc: `+${fmtPct(PASSO.fundo_eleitoral)}% de Caixa 2 ganho por nível. Dinheiro público chamando dinheiro privado.`,
    passo: PASSO.fundo_eleitoral,
    max: 5,
    custoBase: 450,
  },
} satisfies Record<string, MetaUpgradeDef>;

export type MetaUpgradeId = keyof typeof META_UPGRADES;

// Lookup por id vindo da rede (string qualquer)
export const META_UPGRADE_TABLE: Record<string, MetaUpgradeDef | undefined> = META_UPGRADES;

export const META_UPGRADE_LIST: MetaUpgradeDef[] = Object.values(META_UPGRADES);

// Preço do PRÓXIMO nível (nível atual 0-based): base, 2x base, 3x base...
export function metaUpgradeCost(def: MetaUpgradeDef, currentLevel: number): number {
  return def.custoBase * (currentLevel + 1);
}

// Total já gasto num conjunto de níveis (a Reforma Ministerial devolve tudo)
export function metaTotalSpent(upgrades: Record<string, number>): number {
  let total = 0;
  for (const [id, lvl] of Object.entries(upgrades)) {
    const def = META_UPGRADE_TABLE[id];
    if (!def) continue;
    const n = Math.min(Math.max(0, Math.floor(lvl)), def.max);
    for (let i = 0; i < n; i++) total += metaUpgradeCost(def, i);
  }
  return total;
}

// -----------------------------------------------------------------------------
// Contrato HTTP (padrão do ranking: rotas em MetaRoutes.ts)
// -----------------------------------------------------------------------------
export interface PerfilResponse {
  ok: boolean;
  persisted: boolean;          // false = perfil local do navegador (anônimo/dev)
  profile: MetaProfile | null;
  // Feitos (#99/#92/#95): estatísticas vitalícias, conquistas e dossiês.
  // null quando anônimo/sem banco — o cliente usa o localStorage (bs_feitos).
  feitos?: FeitosState | null;
}

export interface GabineteResponse {
  ok: boolean;
  error?: string;
  profile?: MetaProfile;
}
